// Active flight plan store. The "scratch" flight plan that lives on the map.
// User can save it as an immutable Flight (see flightStore) for the dashboard.
//
// Persistence: the active plan is written to localStorage on every
// state change so that:
//   - iOS Safari evicting a backgrounded PWA tab doesn't lose the plan
//   - Pulling out of ForeFlight back into Fyrefly mid-flight finds the
//     plan exactly as you left it
//   - Reloading the page (intentionally or via the SW update flow)
//     preserves the active route
//
// Hydration runs from main.tsx before the React tree mounts so the
// MapView's first paint already has the route.

import { create } from 'zustand';
import { greatCircleNm, bearingDeg } from '../lib/geo';

export type PlanPointKind = 'airport' | 'fix' | 'vor' | 'ndb' | 'navaid' | 'user' | 'procedure';

export interface PlanPoint {
  ident: string;       // ICAO/FAA ident, fix ident, or freeform user label
  lat: number;
  lon: number;
  kind?: PlanPointKind;
  /** Airway designator this waypoint was expanded from (e.g. "V6").
   *  Set by Route Advisor airway expansion; used for future map
   *  color-coding of consecutive waypoints on the same airway. */
  viaAirway?: string;
}

export interface Leg {
  from: PlanPoint;
  to: PlanPoint;
  distanceNm: number;
  trueCourseDeg: number;
}

interface PlanState {
  route: PlanPoint[];
  /** Estimated time of departure (ISO timestamp). */
  etd: string | null;
  /** Cruising altitude in feet MSL. */
  cruiseAlt: number | null;
  /** VFR (odd/even thousands +500) or IFR (odd/even thousands). Local-only
   *  — not persisted to Supabase (no DB schema widening needed). */
  cruiseRule: 'vfr' | 'ifr';
  /** Aircraft profile id (from aircraftStore). */
  aircraftId: string | null;
  /** Performance profile id within the selected aircraft. Null = use
   *  the aircraft's default profile (or first available). Matches the
   *  FlightDetailPage shape so a saved flight inherits the same
   *  selection. 2026-05-17 boss feedback. */
  performanceProfileId: string | null;
  /** Free-text remarks. */
  remarks: string;

  add: (p: PlanPoint) => void;
  insertAt: (idx: number, p: PlanPoint) => void;
  replaceAt: (idx: number, p: PlanPoint) => void;
  remove: (idx: number) => void;
  move: (from: number, to: number) => void;
  /** Replace every NON-airport, NON-procedureTag waypoint between the
   *  first and last airport in the route with `points`. Preserves the
   *  origin / destination airport rows and any SID / STAR / IAP
   *  procedure-loaded points (those carry a procedureTag). Used by
   *  Route Advisor v2 to splice a published or cleared route into
   *  the active plan. (2026-05-17) */
  replaceEnrouteSegment: (points: PlanPoint[]) => void;
  clear: () => void;
  /** Replace the active route with a single leg from `start` to
   *  `target`. Standard EFB 'Direct To' behavior. */
  directTo: (start: PlanPoint, target: PlanPoint) => void;

  setEtd: (iso: string | null) => void;
  setCruiseAlt: (ft: number | null) => void;
  setCruiseRule: (r: 'vfr' | 'ifr') => void;
  setAircraftId: (id: string | null) => void;
  setPerformanceProfileId: (id: string | null) => void;
  setRemarks: (s: string) => void;

  legs: () => Leg[];
  totalNm: () => number;

  /** Restore from localStorage. Called once at app start from main.tsx. */
  hydrate: () => void;
}

const LS_KEY = 'fyrefly:plan:v1';

interface PersistedPlan {
  route: PlanPoint[];
  etd: string | null;
  cruiseAlt: number | null;
  cruiseRule: 'vfr' | 'ifr';
  aircraftId: string | null;
  performanceProfileId: string | null;
  remarks: string;
}

function readPersisted(): PersistedPlan | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedPlan>;
    return {
      route: Array.isArray(parsed.route) ? migrateRoute(parsed.route as PlanPoint[]) : [],
      etd: typeof parsed.etd === 'string' ? parsed.etd : null,
      cruiseAlt: typeof parsed.cruiseAlt === 'number' ? parsed.cruiseAlt : null,
      cruiseRule: parsed.cruiseRule === 'ifr' ? 'ifr' : 'vfr',
      aircraftId: typeof parsed.aircraftId === 'string' ? parsed.aircraftId : null,
      performanceProfileId: typeof parsed.performanceProfileId === 'string' ? parsed.performanceProfileId : null,
      remarks: typeof parsed.remarks === 'string' ? parsed.remarks : '',
    };
  } catch {
    return null;
  }
}

/** Migrate persisted routes saved by older code that didn't tag
 *  synthesized altitude-terminator helpers (VA/CA/FA) with
 *  `procedureTag.hiddenInPlanList = true`. Without the flag those rows
 *  surface in the PlanPanel and FlightBriefing route summary as
 *  "VA@3443ft"-style waypoints, even though they're geometry-only
 *  helpers. Idempotent — already-tagged rows are returned unchanged.
 *
 *  Also clears spurious `isMissedApproach` flags on SID/STAR legs.
 *  Older expansion logic propagated the missed-approach flag whenever
 *  it encountered a VA/CA/FA leg, which on a SID is the legitimate
 *  climb-out (not a missed-approach trigger). Clearing the flag here
 *  prevents the map from rendering SID legs as dashed magenta. */
function migrateRoute(rows: PlanPoint[]): PlanPoint[] {
  let changed = false;
  const out = rows.map((p) => {
    const tag = (p as PlanPoint & { procedureTag?: {
      hiddenInPlanList?: boolean;
      synthetic?: boolean;
      legType?: string;
      routeType?: string;
      isMissedApproach?: boolean;
    } }).procedureTag;
    if (!tag) return p;
    const patch: Record<string, boolean> = {};
    // Synthesized altitude-terminator placeholder — hide in plan list.
    if (
      (tag.synthetic === true || /^(VA|CA|FA)@\d+ft$/.test(p.ident))
      && tag.hiddenInPlanList !== true
    ) {
      patch.hiddenInPlanList = true;
    }
    // SID/STAR legs should never be tagged missed-approach.
    if (
      (tag.routeType === 'SID' || tag.routeType === 'STAR')
      && tag.isMissedApproach === true
    ) {
      patch.isMissedApproach = false;
    }
    if (Object.keys(patch).length === 0) return p;
    changed = true;
    return { ...p, procedureTag: { ...tag, ...patch } } as PlanPoint;
  });
  if (changed && import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.info('[planStore] migrated persisted route: stripped stale procedureTag flags');
  }
  return out;
}

function persist(state: PersistedPlan): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch {
    /* quota — best effort, plan still works in-memory */
  }
}

export const usePlanStore = create<PlanState>((set, get) => ({
  route: [],
  etd: null,
  cruiseAlt: null,
  cruiseRule: 'vfr',
  aircraftId: null,
  performanceProfileId: null,
  remarks: '',

  add: (p) => set({ route: [...get().route, p] }),
  insertAt: (idx, p) => {
    const r = [...get().route];
    r.splice(idx, 0, p);
    set({ route: r });
  },
  replaceAt: (idx, p) => {
    const r = [...get().route];
    r[idx] = p;
    set({ route: r });
  },
  remove: (idx) => {
    type WithTag = PlanPoint & { procedureTag?: { airportIcao?: string } };
    const r = get().route as WithTag[];
    const removed = r[idx];
    if (!removed) return;
    // If we're removing an airport row, ALSO remove any procedure
    // points (SID / STAR / IAP) that were loaded for that airport.
    // Otherwise the procedure legs would orphan in the plan with no
    // anchor — the user has to manually delete each one to clean up.
    // 2026-05-17 boss feedback.
    if (removed.kind === 'airport') {
      const airportIdent = removed.ident;
      const norm = (s: string): string => (s.length === 4 && s.startsWith('K') ? s.slice(1) : s);
      const removedNorm = norm(airportIdent);
      const next = r.filter((p, i) => {
        if (i === idx) return false;
        const tagIcao = p.procedureTag?.airportIcao;
        if (!tagIcao) return true;
        return norm(tagIcao) !== removedNorm;
      });
      set({ route: next });
      return;
    }
    set({ route: r.filter((_, i) => i !== idx) });
  },
  move: (from, to) => {
    const r = [...get().route];
    if (from < 0 || from >= r.length || to < 0 || to >= r.length) return;
    const [item] = r.splice(from, 1);
    r.splice(to, 0, item);
    set({ route: r });
  },

  replaceEnrouteSegment: (points) => {
    type WithTag = PlanPoint & { procedureTag?: { airportIcao?: string } };
    const r = get().route as WithTag[];
    // Walk the route once and split it into:
    //   prefix  — everything up to and INCLUDING the origin airport,
    //             plus any SID procedure rows attached to it.
    //   suffix  — everything from the destination airport onward,
    //             plus any STAR / IAP procedure rows attached to it,
    //             in their original order.
    // The middle (en-route waypoints with no procedureTag) is what we
    // replace.
    const firstAirportIdx = r.findIndex((p) => p.kind === 'airport');
    let lastAirportIdx = -1;
    for (let i = r.length - 1; i >= 0; i--) {
      if (r[i].kind === 'airport') { lastAirportIdx = i; break; }
    }
    if (
      firstAirportIdx < 0
      || lastAirportIdx < 0
      || firstAirportIdx === lastAirportIdx
    ) {
      // Need both endpoints. No-op (caller should guard).
      return;
    }

    // Keep everything BEFORE the destination index that came from a
    // procedureTag for the origin airport (these are SID legs). Drop
    // every non-procedureTag waypoint in the middle.
    const prefix: WithTag[] = [];
    for (let i = 0; i <= firstAirportIdx; i++) prefix.push(r[i]);
    // After origin, before destination: keep procedureTag-tagged rows,
    // drop everything else.
    const middleKept: WithTag[] = [];
    for (let i = firstAirportIdx + 1; i < lastAirportIdx; i++) {
      if (r[i].procedureTag) middleKept.push(r[i]);
    }
    const suffix: WithTag[] = [];
    for (let i = lastAirportIdx; i < r.length; i++) suffix.push(r[i]);

    set({
      route: [...prefix, ...middleKept, ...points, ...suffix],
    });
  },

  clear: () => set({ route: [], etd: null, cruiseAlt: null, cruiseRule: 'vfr', performanceProfileId: null, remarks: '' }),

  directTo: (start, target) => {
    // Replace the active route with [start, target]. Same shape as any
    // other 2-point route so legs() / totalNm() / wind-aware ETE all
    // work without special-casing.
    set({ route: [start, target] });
  },

  setEtd: (iso) => set({ etd: iso }),
  setCruiseAlt: (ft) => set({ cruiseAlt: ft }),
  setCruiseRule: (r) => set({ cruiseRule: r }),
  setAircraftId: (id) => set({
    aircraftId: id,
    // Reset profile selection when aircraft changes — the old id no
    // longer maps onto the new aircraft's profile list.
    performanceProfileId: id === get().aircraftId ? get().performanceProfileId : null,
  }),
  setPerformanceProfileId: (id) => set({ performanceProfileId: id }),
  setRemarks: (s) => set({ remarks: s }),

  legs: () => {
    // Build legs between consecutive NON-placeholder points. A placeholder
    // is a procedure leg whose terminator we couldn't resolve (e.g. a VA /
    // CA / VI leg with no fix coords); it lives in the route for visual
    // inspection but must not participate in distance / bearing math
    // — otherwise its (0, 0) coords produce ~7000 NM phantom legs that
    // catastrophically inflate the total. See Bug #6 (2026-05-16).
    const r = get().route as Array<PlanPoint & {
      procedureTag?: { placeholder?: boolean };
    }>;
    const legs: Leg[] = [];
    // Find non-placeholder points in order.
    const real = r.filter((p) => !p.procedureTag?.placeholder);
    for (let i = 1; i < real.length; i++) {
      const a = real[i - 1];
      const b = real[i];
      legs.push({
        from: a,
        to: b,
        distanceNm: greatCircleNm(a.lat, a.lon, b.lat, b.lon),
        trueCourseDeg: bearingDeg(a.lat, a.lon, b.lat, b.lon),
      });
    }
    return legs;
  },
  totalNm: () => get().legs().reduce((s, l) => s + l.distanceNm, 0),

  hydrate: () => {
    const p = readPersisted();
    if (!p) return;
    set({
      route: p.route,
      etd: p.etd,
      cruiseAlt: p.cruiseAlt,
      cruiseRule: p.cruiseRule,
      aircraftId: p.aircraftId,
      performanceProfileId: p.performanceProfileId,
      remarks: p.remarks,
    });
  },
}));

// Auto-persist on every state change. We subscribe at module scope so
// the writer runs for the lifetime of the page. Listens once and
// shallow-compares the persisted slice; zustand fires this on every
// state mutation including procedure expansion + reorder.
usePlanStore.subscribe((state, prev) => {
  if (state.route === prev.route
      && state.etd === prev.etd
      && state.cruiseAlt === prev.cruiseAlt
      && state.cruiseRule === prev.cruiseRule
      && state.aircraftId === prev.aircraftId
      && state.performanceProfileId === prev.performanceProfileId
      && state.remarks === prev.remarks) {
    return;
  }
  persist({
    route: state.route,
    etd: state.etd,
    cruiseAlt: state.cruiseAlt,
    cruiseRule: state.cruiseRule,
    aircraftId: state.aircraftId,
    performanceProfileId: state.performanceProfileId,
    remarks: state.remarks,
  });
});
